/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { COMPONENTS } from "../../constants/design/spacing";
import { getTradingViewSymbol } from "../../utils/tradingViewSymbol";
import { SettingsContext } from "../../contexts/SettingsContext";
import { Container } from "./style";
import { loadTradingView } from "./loadTradingView";
import {
  buildOverrides,
  buildStudiesOverrides,
  tvIntervalFromLS,
  CHART_BG,
} from "./tvOverrides";
import LightweightCandles, { ITradingViewChartProps } from "./LightweightCandles";

// Monotonic id source — each (re)mount gets its own container id.
let seq = 0;

/**
 * The REAL TradingView "Advanced Real-Time Chart" widget, mounted via tv.js.
 *
 * Colors: candle up/down, background/grid and the volume study are baked in at
 * construction from the live theme (buildOverrides / buildStudiesOverrides read
 * --color-up / --color-down).
 *
 * Live recolor: the FREE tv.js embed applies colors ONLY through the constructor
 * `overrides` — its runtime `applyOverrides` is a no-op. So to recolor when the
 * user changes color scheme we RE-CREATE the widget. To do that reliably (naive
 * re-creation blanks the chart) we:
 *   1. mount once per symbol,
 *   2. on a *genuine* scheme change, bump a DEBOUNCED nonce (coalesces rapid
 *      toggles), which
 *   3. regenerates the container id → React mounts a BRAND-NEW node and the
 *      widget is re-created with the new colors.
 * The user sees a ~1s chart reload on a scheme change, but never has to refresh
 * the page.
 *
 * Resilience: if tv.js is blocked/offline we fall back to the self-hosted,
 * fully-colorable LightweightCandles chart so the price view never goes blank.
 */
function TvAdvancedChart(props: ITradingViewChartProps) {
  const { symbol, isMobileScreen, hidden, load = true } = props;
  const { directionScheme, customDirection } = useContext(SettingsContext);

  const tvSymbol = useMemo(() => getTradingViewSymbol(symbol), [symbol]);
  const schemeKey = `${directionScheme}|${
    customDirection ? JSON.stringify(customDirection) : ""
  }`;

  // Debounced remount trigger: bump only on a genuine scheme change (never on the
  // initial render), coalescing rapid toggles into a single clean re-create.
  const [reloadNonce, setReloadNonce] = useState(0);
  const prevSchemeRef = useRef(schemeKey);
  useEffect(() => {
    if (prevSchemeRef.current === schemeKey) return undefined;
    prevSchemeRef.current = schemeKey;
    const t = window.setTimeout(() => setReloadNonce((n) => n + 1), 250);
    return () => window.clearTimeout(t);
  }, [schemeKey]);

  // Fresh container id per symbol AND per recolor remount → always a clean node.
  const containerId = useMemo(() => {
    seq += 1;
    return `tv_adv_chart_${seq}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tvSymbol, reloadNonce]);

  const widgetRef = useRef<any>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!load || failed) return undefined;
    let cancelled = false;

    loadTradingView()
      .then((TradingView: any) => {
        if (cancelled || !document.getElementById(containerId)) return;
        widgetRef.current = new TradingView.widget({
          container_id: containerId,
          symbol: tvSymbol,
          interval: tvIntervalFromLS(),
          autosize: true,
          theme: "dark",
          style: "1", // candles
          timezone: "Etc/UTC",
          locale: "en",
          hide_side_toolbar: false,
          allow_symbol_change: false,
          // Hide the bottom date-range bar (1D/5D/1M/…/All) — it duplicated the
          // top resolution selector (1m/30m/1h/…) and read as "two intervals".
          withdateranges: false,
          overrides: buildOverrides(),
          studies_overrides: buildStudiesOverrides(),
          loading_screen: { backgroundColor: CHART_BG },
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      try {
        widgetRef.current?.remove?.();
      } catch {
        /* ignore */
      }
      widgetRef.current = null;
    };
  }, [containerId, tvSymbol, load, failed]);

  if (failed) {
    return <LightweightCandles {...props} />;
  }

  return (
    <Container
      style={{
        height: isMobileScreen ? `${COMPONENTS.mobileTvChart}px` : "100%",
        opacity: hidden ? 0 : 1,
        pointerEvents: hidden ? "none" : "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        id={containerId}
        key={containerId}
        style={{ flex: "1 1 auto", minHeight: 0 }}
      />
    </Container>
  );
}

export default TvAdvancedChart;
